/**
 * Gameplay state management hook.
 * Handles the state machine for puzzle interaction.
 */

import { useState, useCallback, useEffect } from 'react';
import { createEngine } from '@daily-puzzle/rules';
import type { DailyResponse, AttemptResponse } from '@daily-puzzle/shared';
import { AttemptResult, Status } from '@daily-puzzle/shared';
import { submitAttempt, revealPuzzle } from '../api/client';

export type GameState =
    | { type: 'loading' }
    | { type: 'error'; message: string }
    | { type: 'ready'; fen: string }
    | { type: 'animating' }
    | { type: 'finished' };

export interface UseGameStateResult {
    gameState: GameState;
    fen: string;
    lastMove: { from: string; to: string } | null;
    handleMove: (from: string, to: string, promotion?: string) => void;
    handleReveal: () => void;
    toast: { message: string; isError?: boolean } | null;
    clearToast: () => void;
}

export function useGameState(
    dailyData: DailyResponse | null,
    onStatusChange?: (status: Status, attemptsUsed: number, stepIndex: number) => void
): UseGameStateResult {
    const [fen, setFen] = useState<string>('');
    const [stepIndex, setStepIndex] = useState(0);
    const [attemptsUsed, setAttemptsUsed] = useState(0);
    const [status, setStatus] = useState<Status>(Status.InProgress);
    const [lastMove, setLastMove] = useState<{ from: string; to: string } | null>(null);
    const [gameState, setGameState] = useState<GameState>({ type: 'loading' });
    const [toast, setToast] = useState<{ message: string; isError?: boolean } | null>(null);

    // Initialize from daily data
    useEffect(() => {
        if (dailyData) {
            setFen(computeCurrentFen(dailyData));
            setStepIndex(dailyData.progress.step_index);
            setAttemptsUsed(dailyData.progress.attempts_used);
            setStatus(dailyData.progress.status as Status);
            setGameState(
                dailyData.progress.status === 'in_progress'
                    ? { type: 'ready', fen: dailyData.puzzle.start_fen }
                    : { type: 'finished' }
            );
        }
    }, [dailyData]);

    // Clear toast after delay
    useEffect(() => {
        if (toast) {
            const timer = setTimeout(() => setToast(null), 2000);
            return () => clearTimeout(timer);
        }
    }, [toast]);

    const handleMove = useCallback(async (from: string, to: string, promotion?: string) => {
        if (!dailyData || gameState.type !== 'ready') return;

        const uci = promotion ? `${from}${to}${promotion}` : `${from}${to}`;

        // Local legality check
        const engine = createEngine(fen);
        if (!engine.isLegalMove(uci)) {
            setToast({ message: 'Illegal', isError: true });
            return;
        }

        setGameState({ type: 'animating' });

        try {
            const idempotencyKey = crypto.randomUUID();
            const response = await submitAttempt(dailyData.date, uci, idempotencyKey);

            handleAttemptResponse(response, from, to);
        } catch (error) {
            setToast({ message: 'Not saved', isError: true });
            setGameState({ type: 'ready', fen });
        }
    }, [dailyData, fen, gameState]);

    const handleAttemptResponse = useCallback((response: AttemptResponse, from: string, to: string) => {
        switch (response.result) {
            case AttemptResult.Illegal:
                setToast({ message: 'Illegal', isError: true });
                setGameState({ type: 'ready', fen: response.fen });
                break;

            case AttemptResult.Incorrect:
                setToast({ message: 'Not it', isError: true });
                setFen(response.fen);
                setStepIndex(response.step_index);
                setAttemptsUsed(response.attempts_used);
                setLastMove(null);
                setGameState({ type: 'ready', fen: response.fen });
                onStatusChange?.(response.status, response.attempts_used, response.step_index);
                break;

            case AttemptResult.Correct:
                setFen(response.fen);
                setStepIndex(response.step_index);
                setLastMove({ from, to });
                // TODO: Animate auto-reply if present
                setGameState({ type: 'ready', fen: response.fen });
                onStatusChange?.(response.status, response.attempts_used, response.step_index);
                break;

            case AttemptResult.Solved:
                setFen(response.fen);
                setStepIndex(response.step_index);
                setStatus(Status.Solved);
                setLastMove({ from, to });
                setGameState({ type: 'finished' });
                onStatusChange?.(response.status, response.attempts_used, response.step_index);
                break;

            case AttemptResult.Missed:
                setFen(response.fen);
                setAttemptsUsed(response.attempts_used);
                setStatus(Status.Missed);
                setGameState({ type: 'finished' });
                onStatusChange?.(response.status, response.attempts_used, response.step_index);
                break;
        }
    }, [onStatusChange]);

    const handleReveal = useCallback(async () => {
        if (!dailyData) return;

        setGameState({ type: 'animating' });

        try {
            const idempotencyKey = crypto.randomUUID();
            const response = await revealPuzzle(dailyData.date, idempotencyKey);

            setFen(response.fen);
            setStatus(Status.Revealed);
            setGameState({ type: 'finished' });
            onStatusChange?.(Status.Revealed, attemptsUsed, stepIndex);

            // TODO: Animate solution playback
        } catch (error) {
            setToast({ message: 'Failed to reveal', isError: true });
            setGameState({ type: 'ready', fen });
        }
    }, [dailyData, fen, attemptsUsed, stepIndex, onStatusChange]);

    const clearToast = useCallback(() => setToast(null), []);

    return {
        gameState,
        fen,
        lastMove,
        handleMove,
        handleReveal,
        toast,
        clearToast,
    };
}

/**
 * Compute current FEN based on progress through puzzle.
 */
function computeCurrentFen(dailyData: DailyResponse): string {
    const { puzzle, progress } = dailyData;

    if (progress.step_index === 0) {
        return puzzle.start_fen;
    }

    const engine = createEngine(puzzle.start_fen);

    for (let i = 0; i < progress.step_index; i++) {
        const step = puzzle.steps[i];
        if (!step) break;

        engine.makeMove(step.your_moves_uci[0]!);
        if (step.auto_reply_uci) {
            engine.makeMove(step.auto_reply_uci);
        }
    }

    return engine.fen();
}
